import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Heading, Line } from '@mm/components';
import { useMailchimpConnectionCRUDActions } from '@mm/state/actions/MailchimpConnectionsCRUDActions';
import { useMailchimpCRUDActions } from '@mm/state/actions/MailchimpCRUDActions';
import {
  MailchimpAccount,
  MailchimpList,
} from '@mm/state/model/MailchimpModel';
import AttributeModel from '@mm/state/model/AttributeModel';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { ConnectionVariableModel } from '@mm/state/model/ConnectionVariableModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectAttributes } from '@mm/state/selectors/attributeSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import {
  selectMailchimpLists,
  selectMailchimpAccounts,
  selectMailchimpListMergeFields,
  selectMailchimpTotalListItems,
} from '@mm/state/selectors/mailchimpSelectors';
import { ReactComponent as ArrowRightLongIcon } from 'assets/icons/arrow-right-long.svg';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../../../../common/components/PrimarySelect/PrimarySelect';
import {
  IGNORELIST,
  PHONE_ATTRIBUTE_NAME,
  phoneAttribute,
} from '../../../../../../../../../common/modals/ImportContantListModal/ImportContantListModal';
import {
  blankVariable,
  canBeTestedMailchimp,
} from '@mm/state/helpers/connection-helpers';
import { uniqBy, omit, isEmpty } from 'lodash';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { IndexAttributeNameMap } from '@mm/state/modules/modalsState/props';

export const useEditMailchimp = (
  setConnection: (connection: ConnectionModel) => void,
  connection?: ConnectionModel,
  isSlideout?: boolean,
) => {
  const { update } = useMailchimpConnectionCRUDActions();
  const { openMailchimpTestConnectionModal } = useModalActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { getLists, getMoreLists, getListMergeFields, resetListMergeFields } =
    useMailchimpCRUDActions();
  const lists = useSelector(selectMailchimpLists);
  const listMergeFields = useSelector(selectMailchimpListMergeFields);
  const accounts = useSelector(selectMailchimpAccounts);
  const totalListItems = useSelector(selectMailchimpTotalListItems);
  const allAccounts = accounts;
  const [isFetching, setIsFetching] = useState(false);

  const [selectedAccount, setSelectedAccount] = useState<MailchimpAccount>();
  const [selectedList, setSelectedList] = useState<MailchimpList>();
  const [selectedEmailVariable, setSelectedEmailVariable] =
    useState<AttributeModel>();
  const [emailVariable, setEmailVariable] = useState<any>();
  const filtertedAccounts = accounts.filter(acc => !acc.readOnly);
  const isReadOnlyAccount = !!accounts.find(
    acc => acc.id === connection?.mailchimpAccessTokenId && acc.readOnly,
  );

  const mapFiltered = listMergeFields
    .filter(option => option.tag !== 'ADDRESS')
    .map(column => {
      const variableColumn = connection?.variables?.find(
        variable => variable.sentAs === column.tag,
      );
      if (variableColumn) {
        return { ...variableColumn, ...{ mailchimpName: column.name } };
      }
      return {
        sentAs: column.tag,
        name: column.name,
        mailchimpName: column.name,
      };
    });

  const mappedVars = connection?.variables?.map(variable => {
    return {
      sentAs: variable.sentAs,
      name: variable.name,
      mailchimpName: variable.name,
    };
  });
  const mappings =
    mapFiltered.length && !isReadOnlyAccount ? mapFiltered : mappedVars;

  const isMyAccount = !!(
    connection?.mailchimpAccessTokenId &&
    accounts.find(account => account.id === connection.mailchimpAccessTokenId)
  );

  useEffect(() => {
    if (connection?.mailchimpAccessTokenId) {
      const currAccount = allAccounts.find(
        account => account.id === connection.mailchimpAccessTokenId,
      );
      if (currAccount?.id) {
        setSelectedAccount(currAccount);
      }
    }
    if (connection?.mailchimpAccountEmail) {
      const currAccount = allAccounts.find(
        account => account.email === connection.mailchimpAccountEmail,
      );
      if (currAccount?.id) {
        setSelectedAccount(currAccount);
      }
    }

    if (connection?.data?.listId) {
      setSelectedList(lists.find(list => list.id === connection.data.listId));
    } else {
      setSelectedList(undefined);
    }

    if (connection?.variables?.length) {
      setSelectedEmailVariable(
        connection.variables.find(variable => variable.sentAs === 'EMAIL'),
      );
    } else if (connection?.data?.listId) {
      const emailOption = options.find(
        option => option.name.toLocaleUpperCase() === 'EMAIL',
      );
      onSelectEmailVariable(emailOption || options[0], connection);
    } else {
      setSelectedEmailVariable(undefined);
    }
  }, [connection, allAccounts, lists]);

  const updateConnection = async (updatedFields: Partial<ConnectionModel>) => {
    if (!connection) {
      return;
    }
    const updatedConnection = await update(activeFbPageId, {
      ...connection,
      validationMessages: null,
      ...updatedFields,
    });

    if (updatedConnection) {
      setConnection(updatedConnection);
    }
  };

  const getVariablesToUpdate = (name: string, sentAs: string) => {
    let idx = 0;
    if (connection?.variables) {
      idx = connection.variables.findIndex(
        variable => variable.sentAs === sentAs || variable.name === name,
      );

      if (isEmpty(name)) {
        return [
          ...connection.variables.slice(0, idx),
          ...connection.variables.slice(idx + 1),
        ];
      }

      return [
        ...connection.variables.slice(0, idx),
        {
          ...blankVariable(),
          name,
          sentAs,
        },
        ...connection.variables.slice(idx + 1),
      ];
    }
  };
  const updateVariables = (name: string, sentAs: string) => {
    updateConnection({
      variables: getVariablesToUpdate(name, sentAs),
    });
  };

  const onSelectAccount = async item => {
    resetListMergeFields();
    setSelectedAccount(item);
    await updateConnection({
      mailchimpAccessTokenId: item.id,
      mailchimpAccountEmail: item.email,
      data: {},
      variables: [],
    });
    getLists(item.id);
  };

  const [map, setMap] = useState<IndexAttributeNameMap>({});
  const attributes = useSelector(selectAttributes);

  const containsInMap = (tag: string) => {
    return Object.values(map).includes(tag);
  };

  const buildVariables = () => {
    if (!connection || !connection?.variables) return [];
    const variables: ConnectionVariableModel[] = [];
    const tagFirstName = listMergeFields.find(option => option.tag === 'FNAME');
    const tagLastName = listMergeFields.find(option => option.tag === 'LNAME');
    const tagPhone = listMergeFields.find(option => option.tag === 'PHONE');
    const optionFirstName = options.find(
      option => option.name === 'first name',
    );
    const optionLastName = options.find(option => option.name === 'last name');
    const optionPhone = options.find(option => option.name === 'PHONE');

    if ((tagFirstName && optionFirstName) || containsInMap('first name')) {
      variables.push({
        ...blankVariable(),
        name: 'first name',
        sentAs: 'FNAME',
      });
    }
    if ((tagLastName && optionLastName) || containsInMap('last name')) {
      variables.push({
        ...blankVariable(),
        name: 'last name',
        sentAs: 'LNAME',
      });
    }
    if ((tagPhone && optionPhone) || containsInMap('PHONE')) {
      variables.push({
        ...blankVariable(),
        name: 'PHONE',
        sentAs: 'PHONE',
      });
    }

    if (emailVariable) {
      variables.push(emailVariable);
    }

    const filteredVariables = uniqBy(
      [...connection.variables, ...variables],
      el => el.sentAs,
    );
    return filteredVariables;
  };

  useEffect(() => {
    if (!connection?.variables) return;
    const variableNames = connection.variables.map(variable => variable.name);
    setMap({ ...variableNames });
  }, [connection]);

  const updateAttributes = async () => {
    await updateConnection({
      variables: buildVariables(),
    });
  };

  const onSelectList = async (item, account, connection) => {
    setSelectedList(item);
    const { data } = connection;
    await updateConnection({
      data: { ...data, ...{ listId: item.id, name: item.name } },
    });
    getListMergeFields(account.id, item.id);
  };

  useEffect(() => {
    if (!listMergeFields.length) return;
    updateAttributes();
  }, [listMergeFields]);

  const onSelectEmailVariable = async (item, connection) => {
    setSelectedEmailVariable(item);
    const { variables } = connection as any;
    const filteredVariables = variables?.filter(
      variable => variable?.sentAs !== 'EMAIL',
    );
    const variable = {
      ...blankVariable(),
      name: item.name,
      sentAs: 'EMAIL',
    };
    await updateConnection({
      variables: [...filteredVariables, variable],
    });
  };

  const onClickActivate = async () => {
    await updateConnection({
      active: true,
    });
  };

  const onScrollEnd = async () => {
    const totalLists = lists.length;
    const isLastPage = totalLists >= totalListItems;

    if (isLastPage || isFetching) return;
    if (isMyAccount && connection?.mailchimpAccessTokenId) {
      setIsFetching(true);
      await getMoreLists(connection?.mailchimpAccessTokenId, totalLists);
      setIsFetching(false);
    }
  };

  const listPlaceholder = () => connection?.data?.name || 'Choose Audience';

  const onClickTestConnection = () => {
    if (connection) {
      openMailchimpTestConnectionModal({ connection, onClickActivate });
    }
  };

  const whatToDoOptions: DropdownOption[] = [
    {
      id: 1,
      name: 'Add/Update Subscriber',
    },
  ];

  const [whatToDoOptionSelected, setWhatToDoOptionSelected] = useState(
    whatToDoOptions[0],
  );

  const whenBeTriggeredOptions: DropdownOption[] = [
    {
      id: 1,
      name: ' When the email attribute is set',
    },
  ];

  const [whenBeTriggeredOptionSelected, setWhenBeTriggeredOptionSelected] =
    useState(whenBeTriggeredOptions[0]);

  const withPhone = attributes.some(
    ({ name }) => name === PHONE_ATTRIBUTE_NAME,
  );
  const attrs = !withPhone ? [...attributes, phoneAttribute] : attributes;
  const attrsWithBlankVariable = [blankVariable(), ...attrs];

  const selectedNames = Object.values(map);
  const options = attrs
    .filter(attr => !selectedNames.includes(attr.name))
    .filter(attr => !IGNORELIST.includes(attr.name));

  const optionsWithBlankVariable = attrsWithBlankVariable
    .filter(attr => !selectedNames.includes(attr.name))
    .filter(attr => !IGNORELIST.includes(attr.name));

  const onAttributeSelected = (
    { name }: AttributeModel,
    sentAs: string,
    index: number,
  ) => {
    const next = isEmpty(name) ? omit(map, [index]) : { ...map, [index]: name };
    setMap(next);

    updateVariables(name, sentAs);
  };

  const disabled = !connection
    ? false
    : !canBeTestedMailchimp(connection, true);

  const buttonClassName = isSlideout
    ? 'edit-google-sheet-item__dropdown__slideout'
    : 'edit-google-sheet-item__dropdown';

  const configs = [
    {
      header: 'Connection Settings',
      children: (
        <div>
          <div className="edit-google-sheet-item__content">
            <div className="edit-google-sheet-item__section">
              <Heading
                className="edit-google-sheet-item__section-name"
                size={5}
                text="What would you like to do?"
              />
              <PrimarySelect
                className={buttonClassName}
                options={whatToDoOptions}
                value={whatToDoOptionSelected}
                onChange={setWhatToDoOptionSelected}
                dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
              />
            </div>
            <div className="edit-google-sheet-item__section">
              <Heading
                className="edit-google-sheet-item__section-name"
                size={5}
                text="When will the integration be triggered?"
              />
              <PrimarySelect
                className={buttonClassName}
                options={whenBeTriggeredOptions}
                value={whenBeTriggeredOptionSelected}
                onChange={setWhenBeTriggeredOptionSelected}
                dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
              />
            </div>
            <Line />
            <div className="edit-google-sheet-item__section">
              <Heading
                size={5}
                className="edit-google-sheet-item__set-up-section-header"
                text="Set up your connection"
              />
              <Heading
                size={5}
                className="edit-google-sheet-item__set-up-header"
                text="Choose an account"
              />
              <PrimarySelect
                className={buttonClassName}
                placeholder="Choose an account"
                options={filtertedAccounts}
                value={selectedAccount}
                onChange={onSelectAccount}
                disabled={isReadOnlyAccount}
                displayNameKey="email"
                dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
              />
              <Heading
                size={5}
                className="edit-google-sheet-item__set-up-header"
                text="Select an audience"
              />
              <PrimarySelect
                className={buttonClassName}
                placeholder={listPlaceholder()}
                options={lists}
                value={selectedList}
                onChange={item =>
                  onSelectList(item, selectedAccount, connection)
                }
                dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
                disabled={isReadOnlyAccount}
                isLoading={isFetching}
                onScrollEnd={onScrollEnd}
              />
              <Heading
                size={5}
                className="edit-google-sheet-item__set-up-header"
                text="Choose attribute containing subscriber email"
              />
              <PrimarySelect
                className={buttonClassName}
                placeholder="Subscriber Email"
                options={options}
                value={selectedEmailVariable}
                onChange={item => onSelectEmailVariable(item, connection)}
                dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
                showSearchFilter
                customOptionsPosition={isSlideout ? 'top' : 'bottom'}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      header: (
        <div className="edit-google-sheet-item__menu">
          Update field or create new field with attribute values:
        </div>
      ),
      children: (
        <div className="edit-google-sheet-item__content">
          {mappings?.length ? (
            <div className="edit-google-sheet-item__mapper-container">
              <div className="edit-google-sheet-item__mapper-header">
                <div className="edit-google-sheet-item__mapper-header-title">
                  Mailchimp column
                </div>
                <div className="edit-google-sheet-item__mapper-header-title">
                  Customers.ai attribute
                </div>
              </div>
              <div className="edit-google-sheet-item__mapper-rows">
                {mappings?.map((column, index: number) => (
                  <div
                    className="edit-google-sheet-item__mapper-row"
                    key={index}
                  >
                    <div className="edit-google-sheet-item__mapper-column-name">
                      {column.mailchimpName}
                    </div>
                    <span className="edit-google-sheet-item__mapper-arrow">
                      <ArrowRightLongIcon />
                    </span>
                    <div className="edit-google-sheet-item__mapper-attribute">
                      <PrimarySelect
                        className="edit-google-sheet-item__mapper-attribute-selector"
                        dropdownClassName="edit-google-sheet-item__mapper-attribute-selector__dropdown"
                        options={optionsWithBlankVariable}
                        value={attrsWithBlankVariable.find(
                          ({ name }) => name === column.name,
                        )}
                        customOptionsPosition={isSlideout ? 'top' : 'bottom'}
                        onChange={v =>
                          onAttributeSelected(
                            v as AttributeModel,
                            column.sentAs ?? '',
                            index + 1,
                          )
                        }
                        showSearchFilter
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="edit-google-sheet-item__warning">
              <ExclamationCircleFilled className="edit-google-sheet-item__exclamation-filled-icon" />
              Please fill in previous steps to continue
            </div>
          )}
          {!isSlideout && mappings?.length && (
            <div className="edit-google-sheet-item__mapper-actions">
              <PrimaryButton
                color={ButtonColor.blue}
                className="edit-google-sheet-item__mapper-activate-button"
                onClick={onClickActivate}
                disabled={connection?.active || disabled}
              >
                Activate
              </PrimaryButton>
              <PrimaryButton
                color={ButtonColor.secondaryBlue}
                onClick={onClickTestConnection}
                disabled={disabled}
              >
                Test Connection
              </PrimaryButton>
            </div>
          )}
        </div>
      ),
    },
  ];

  return { configs };
};
